import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, Quote } from "lucide-react";

/**
 * Neon-arcade daily quote banner. Renders nothing if the feature is off or
 * the fetch fails — home page should never break because of this widget.
 *
 * Visual: matte black card · red scanline border · rotating LIVE / DO / BE
 * kicker that changes each day · huge display quote · founder motto footer.
 */
export default function DailyQuoteBanner() {
  const [q, setQ] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/daily-quote");
        if (alive) setQ(data);
      } catch {
        /* silent — home page should never break */
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (!loaded) return null;
  if (!q || q.enabled === false || !q.text) return null;

  const tenet = q.tenet || "LIVE LIFE";
  // Explicitly format in Eastern Time — the banner is always on Loot time.
  const dayLabel = q.date_key
    ? new Date(`${q.date_key}T12:00:00-05:00`).toLocaleDateString("en-US", {
        timeZone: "America/New_York",
        month: "short",
        day: "numeric",
      }).toUpperCase()
    : "";

  return (
    <section
      className="relative overflow-hidden rounded-[2px] border"
      style={{
        background: "linear-gradient(180deg, #0b0b0b 0%, #0F0F0F 100%)",
        borderColor: "#E53935",
        boxShadow:
          "0 0 0 1px #E53935, 0 0 32px -4px rgba(229,57,53,0.45), inset 0 0 48px rgba(229,57,53,0.10)",
      }}
      data-testid="daily-quote-banner"
    >
      {/* Scanline texture */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.07] mix-blend-overlay"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, rgba(255,255,255,0.6) 0, rgba(255,255,255,0.6) 1px, transparent 1px, transparent 3px)",
        }}
      />
      {/* Red corner ticker */}
      <div
        className="absolute top-0 left-0 px-3 py-1 font-mono text-[10px] uppercase tracking-widest"
        style={{
          color: "#F5F2E8",
          background: "#E53935",
          letterSpacing: "0.22em",
        }}
      >
        Get Hyped · {dayLabel}
      </div>

      <div className="px-6 md:px-10 py-10 md:py-14 flex flex-col items-start gap-5 relative">
        {/* Rotating kicker — changes every day */}
        <div className="flex items-center gap-2 mt-4 md:mt-2">
          <Sparkles size={16} style={{ color: "#E53935" }} className="animate-pulse" />
          <span
            className="font-display uppercase text-2xl md:text-3xl"
            style={{
              color: "#E53935",
              textShadow:
                "0 0 8px rgba(229,57,53,0.7), 0 0 24px rgba(229,57,53,0.4)",
              letterSpacing: "0.04em",
            }}
            data-testid="daily-quote-tenet"
          >
            {tenet}
          </span>
          <span className="font-mono text-xs text-[#F5F2E8]/40">//</span>
          <span className="font-mono text-xs text-[#F5F2E8]/60 uppercase tracking-wider">
            today's tenet
          </span>
        </div>

        {/* The quote itself — big, bold, left-aligned */}
        <blockquote
          className="font-display uppercase leading-[0.95] text-[clamp(1.9rem,5vw,3.6rem)]"
          style={{ color: "#F5F2E8" }}
          data-testid="daily-quote-text"
        >
          <Quote
            size={28}
            className="inline -mt-2 mr-2"
            style={{ color: "#D7A62A" }}
          />
          <span>{q.text}</span>
        </blockquote>

        {/* Footer — motto + source */}
        <div className="flex items-center gap-3 flex-wrap pt-2 border-t border-[#F5F2E8]/10 w-full">
          <span className="font-mono text-xs text-[#F5F2E8]/50 uppercase tracking-widest">
            — {q.author || "The Local Loot"}
          </span>
          <span className="font-mono text-[10px] text-[#F5F2E8]/30">|</span>
          <span className="font-mono text-xs" style={{ color: "#76B82A" }}>
            Live Life · Do Good · Be Happy
          </span>
        </div>
      </div>
    </section>
  );
}
